import './ThemeList.scss';

import React, { ChangeEvent, FC, SyntheticEvent, useContext, useEffect } from 'react';
import { AppContext, NotificationType } from 'components/AppContext/AppContext';
import NavHeading, { NavHeadingProps } from 'components/NavHeading/NavHeading';
import TableActionButtonGroup from 'components/TableActionButtonGroup/TableActionButtonGroup';
import TableSearch from 'components/TableSearch/TableSearch';
import { NavHeadings } from 'constants/Constants';
import { useDataSet } from 'customHooks/useDataSet';
import { useModifyEntityService } from 'customHooks/useModifyEntityService';
import { useTokenExpiredHandler } from 'customHooks/useTokenExpiredHandler';
import DataSetOperators, { SearchType, SortDirection } from 'interfaces/DataSetOperators';
import DataWrapper from 'interfaces/DataWrapper';
import { Entities } from 'models/Entities';
import { LogLevel } from 'models/LogLevel';
import { Theme } from 'models/Theme';
import moment from 'moment';
import { Col, Row } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import { Link } from 'react-router-dom';


// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ThemeListProps { }

const ThemeList: FC<ThemeListProps> = () => {
  const { configRepo, selectedProjectId, setIsProjectSelectable, notify, sendLog, projects } = useContext(AppContext);
  const initialParams: DataSetOperators = {
    SortBy: 'scheduledDate',
    SortDirection: SortDirection.Desc,
    Top: 10
  };
  const { dataSet, setOperators, operators } = useDataSet<Theme>(
    configRepo!.SCHEDULE_API_BASE_URI,
    Entities.Themes,
    selectedProjectId,
    initialParams,
  );
  const dataWrapper: DataWrapper<Theme> =
    dataSet.responseData as DataWrapper<Theme>;
  const themes: Theme[] = dataWrapper?.data;
  const [resetPaginationToggle, setResetPaginationToggle] = React.useState(false);
  const [filterText, setFilterText] = React.useState('');

  useEffect(() => {
    setIsProjectSelectable(true);
  }, []);

  useEffect(() => {
    if (operators.ProjectId !== selectedProjectId) {
      setResetPaginationToggle(true);
      const ops = { ...operators };
      ops.Skip = 0;
      ops.ProjectId = selectedProjectId;
      ops.SortBy = 'scheduledDate';
      ops.SortDirection = SortDirection.Desc;
      setOperators(ops);
    }
  }, [selectedProjectId]);

  const onTokenExpired = useTokenExpiredHandler();
  const { deleteEntity } = useModifyEntityService<Theme>(
    configRepo!.SCHEDULE_API_BASE_URI,
    Entities.Documents,
    sendLog,
    onTokenExpired,
  );

  const onDelete = async (id: number): Promise<void> => {
    if (await deleteEntity(id)) {
      notify('Theme has been deleted successfully.', NotificationType.Success);
      sendLog(`deleted successfully theme: ${id}`, LogLevel.info);
      const ops = { ...operators };
      setOperators(ops);
    } else {
      notify('Theme has not been deleted successfully.', NotificationType.Error);
      sendLog(`failed to delete theme: ${id}`, LogLevel.error);
    }
  };

  const columns = [
    {
      name: 'Actions',
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: (row: any) => (
        <TableActionButtonGroup
          entityContainerUrl={Entities.Themes}
          entityId={row.id}
          key={row.id}
          onDelete={onDelete}
          isViewEnabled={false}
        />
      ),
      ignoreRowClick: true,
      allowOverflow: false,
      button: true,
      className: 'padd-left',
      width: '150px',
    },
    {
      name: 'Date',
      selector: (row: any) =>
        row.scheduledDate ? moment(new Date(row.scheduledDate)).format('MM/DD/YYYY') : 'UNPLANNED',
      type: 'scheduledDate',
      sortable: true,
      width: '120px',
    },
    {
      name: 'Name',
      selector: (row: any) => row.name,
      type: 'name',
      sortable: true,
      width: '200px',
    },
    {
      name: 'Description',
      selector: (row: any) => row.description,
      type: 'description',
      sortable: true,
      width: '300px',
    }
  ];

  const handleSort = (column: any, sortDirection: any) => {
    const ops = { ...operators };
    ops.SortBy = column.type;
    ops.SortDirection = sortDirection === 'asc' ? SortDirection.Asc : SortDirection.Desc;
    setOperators(ops);
  };

  const handlePerRowsChange = (page: number) => {
    const ops = { ...operators };
    ops.Top = page;
    setOperators(ops);
  };

  const handlePageChange = (page: number) => {
    const ops = { ...operators };
    if (page === 1) {
      ops.Skip = 0;
    } else {
      ops.Skip = ops.Top! * (page - 1);
    }
    setOperators(ops);
  };

  // see: https://react-data-table-component.netlify.app/?path=/docs/examples-filtering--filtering
  const onSearchTextChanged = (e: ChangeEvent<HTMLInputElement>) => {
    setFilterText(e.target.value);
  };

  const applySearch = (terms: string | undefined) => {
    const ops = { ...operators };
    if (terms) {
      ops.SearchProperty = 'Name|Description';
      ops.SearchType = SearchType.Contains;
      ops.SearchValue = terms;
    } else {
      ops.SearchProperty = undefined;
      ops.SearchType = undefined;
      ops.SearchValue = undefined;
    }
    // this is necessary
    // test: switch project A > search 'T' > switch project B > clear search
    // => results are project A unfiltered without this
    ops.ProjectId = selectedProjectId;
    setOperators(ops);
  };

  const subHeaderComponentMemo = React.useMemo(() => {
    const handleClear = () => {
      if (filterText) {
        setResetPaginationToggle(!resetPaginationToggle);
        setFilterText('');
        applySearch(undefined);
      }
    };

    const onFilter = (e: SyntheticEvent) => {
      e.preventDefault();
      applySearch(filterText);
    };

    return (
      <div className="action-panel">
        <Row>
          <Col md={6} xs={6} className="g-0">
            <TableSearch
              onFilter={onFilter}
              onClear={handleClear}
              filterPropertyName="Name or Description"
              searchText={filterText}
              onSearchTextChanged={onSearchTextChanged}
            />
          </Col>
          <Col md={6} xs={6}>
            <Link className="btn btn-primary" to='/themes/edit/new'>
              <i className='bi bi-plus-lg pr-1' />
              New Theme
            </Link>
          </Col>
        </Row>
      </div>
    );
  }, [filterText, resetPaginationToggle, selectedProjectId]);

  const heading: NavHeadingProps = {
    title: `${projects.find(p => p.id === selectedProjectId)?.name} Themes`,
    ...NavHeadings.Themes,
  };

  return (
    <div className=''>
      <NavHeading {...heading} />
      <div className="data-collection">
        <DataTable
          columns={columns}
          defaultSortFieldId="2"
          defaultSortAsc={false}
          className="tbl-themes"
          data={themes}
          sortServer
          onSort={handleSort}
          pagination
          paginationServer
          paginationTotalRows={dataWrapper?.metaData?.totalRecordCount ?? 0}
          paginationResetDefaultPage={resetPaginationToggle}
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          subHeader
          subHeaderComponent={subHeaderComponentMemo}
          persistTableHead
        // fixedHeader
        // fixedHeaderScrollHeight="500px"
        // dense
        />
      </div>
    </div>
  );
}

export default ThemeList;
